import { getSuiteApps, launchSuiteApp, type SuiteApp } from "./suite-config";
import { ICONS } from "./icons";

/**
 * <cybears-suite-banner
 *   current-app-id="cx3"
 *   tenant-id="..."
 *   logo-src="/logo-white.svg"
 *   cx3-origin="https://cx3.cybears.net"
 *   cmss-launch-url="https://cx3-xmss.cybears.net/login?sso=auto"
 *   xawareness-launch-url="https://xawareness.cybears.net/fr/auth/signin?sso=auto"
 * ></cybears-suite-banner>
 *
 * Renders into a shadow root so its CSS never collides with the host app's
 * (Tailwind/shadcn in every consumer today). Sits as an additive strip above
 * whatever header the host already renders — it does not replace it.
 *
 * Always dark, regardless of the host app's own light/dark theme — this is a
 * suite-level brand bar (same idea as GitHub's or Atlassian's top bar), not
 * part of any one product's UI, so it doesn't follow it. Pass a light logo
 * variant via `logo-src` (it always renders on a dark background).
 *
 * `cx3-origin`/`cmss-launch-url` (SSO-SEC-014): every non-CX3 tile's URL is
 * built from `cx3-origin`, and CMSS's tile from `cmss-launch-url` directly.
 * Both default to localhost when unset, so local dev needs no config, but
 * every real deployment must set them from its own env config or the
 * banner can only ever navigate to the viewer's own machine.
 *
 * Emits `suite-navigate` with { app, tenantId } before navigating, so a host
 * can call preventDefault() on the event and handle the transition itself
 * (e.g. through its own router) instead of the built-in window.location/open.
 */
export class CybearsSuiteBanner extends HTMLElement {
  static get observedAttributes() {
    return [
      "current-app-id",
      "tenant-id",
      "logo-src",
      "cx3-origin",
      "cmss-launch-url",
      "xawareness-launch-url",
      "user-initials",
      "show-purple-number",
      "app-title",
      "app-subtitle",
    ];
  }

  private root: ShadowRoot;
  private open = false;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  attributeChangedCallback() {
    this.render();
  }

  private handleOutsideClick = (e: MouseEvent) => {
    if (this.open && !e.composedPath().includes(this)) {
      this.open = false;
      this.render();
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.open) {
      this.open = false;
      this.render();
    }
  };

  // Named *Attr rather than properties directly to prevent React 19 property assignment collisions.
  private currentAppIdAttr() {
    return this.getAttribute("current-app-id") ?? "";
  }

  private tenantIdAttr() {
    return this.getAttribute("tenant-id") ?? undefined;
  }

  private logoSrcAttr() {
    return this.getAttribute("logo-src");
  }

  private cx3OriginAttr() {
    return this.getAttribute("cx3-origin") ?? undefined;
  }

  private cmssLaunchUrlAttr() {
    return this.getAttribute("cmss-launch-url") ?? undefined;
  }

  private xawarenessLaunchUrlAttr() {
    return this.getAttribute("xawareness-launch-url") ?? undefined;
  }

  private userInitialsAttr() {
    return this.getAttribute("user-initials") || "SB";
  }

  private showPurpleNumberAttr() {
    const attr = this.getAttribute("show-purple-number");
    if (attr !== null) return attr === "true";
    return this.currentAppIdAttr() === "cx3";
  }

  private appTitleAttr() {
    return this.getAttribute("app-title");
  }

  private appSubtitleAttr() {
    return this.getAttribute("app-subtitle");
  }

  private suiteApps(): SuiteApp[] {
    return getSuiteApps(this.cx3OriginAttr(), this.cmssLaunchUrlAttr(), this.xawarenessLaunchUrlAttr());
  }

  private currentApp(): SuiteApp | undefined {
    return this.suiteApps().find((a) => a.id === this.currentAppIdAttr());
  }

  private getProductIdentity(app?: SuiteApp) {
    const customTitle = this.appTitleAttr();
    const customSubtitle = this.appSubtitleAttr();
    const appId = this.currentAppIdAttr();

    let titleHtml = "";
    if (customTitle) {
      titleHtml = escapeHtml(customTitle);
    } else if (appId === "cx3") {
      titleHtml = `CX<sup class="product-sup">3</sup>`;
    } else {
      titleHtml = escapeHtml(app?.name ?? "CYBEARS");
    }

    let subtitle = "";
    if (customSubtitle) {
      subtitle = customSubtitle;
    } else if (appId === "cx3") {
      subtitle = "CYBEARS COMMAND CENTRE";
    } else if (appId === "cmss") {
      subtitle = "EXTENDED MATURITY & SECURITY SYSTEM";
    } else if (app?.tagline) {
      subtitle = `CYBEARS ${app.tagline.toUpperCase()}`;
    } else {
      subtitle = "CYBEARS SUITE";
    }

    return { titleHtml, subtitle };
  }

  private handleTileClick(app: SuiteApp) {
    const currentAppId = this.currentAppIdAttr();
    if (app.id === currentAppId) return;
    const tenantId = this.tenantIdAttr();
    const detail = { app, tenantId };
    const evt = new CustomEvent("suite-navigate", {
      detail,
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    const notPrevented = this.dispatchEvent(evt);
    this.open = false;
    this.render();
    if (notPrevented) {
      launchSuiteApp(app, currentAppId, tenantId);
    }
  }

  private render() {
    const current = this.currentApp();
    const logoSrc = safeImgSrc(this.logoSrcAttr());
    const { titleHtml, subtitle } = this.getProductIdentity(current);
    const userInitials = this.userInitialsAttr();
    const showPurpleNumber = this.showPurpleNumberAttr();

    this.root.innerHTML = `
      <style>${STYLE}</style>
      <div class="bar">
        <div class="left">
          <div class="menu-anchor">
            <button class="waffle-btn" aria-label="Switch product" aria-expanded="${this.open}">
              <span></span><span></span><span></span>
              <span></span><span></span><span></span>
              <span></span><span></span><span></span>
            </button>
            ${this.open ? this.renderPanel() : ""}
          </div>

          ${logoSrc ? `<img class="logo" src="${escapeHtml(logoSrc)}" alt="Cybears" />` : `<span class="wordmark">CYBEARS</span>`}

          <div class="divider"></div>

          <div class="product-brand">
            <div class="product-title">${titleHtml}</div>
            <div class="product-accent-line"></div>
            <div class="product-subtitle">${escapeHtml(subtitle)}</div>
          </div>
        </div>

        <div class="right">
          ${showPurpleNumber ? `
            <button class="purple-btn" aria-label="Purple Number">
              Purple Number
            </button>
          ` : ""}

          <button class="icon-btn theme-toggle" aria-label="Toggle theme">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="4"></circle>
              <path d="M12 2v2"></path>
              <path d="M12 20v2"></path>
              <path d="m4.93 4.93 1.41 1.41"></path>
              <path d="m17.66 17.66 1.41 1.41"></path>
              <path d="M2 12h2"></path>
              <path d="M20 12h2"></path>
              <path d="m6.34 17.66-1.41 1.41"></path>
              <path d="m19.07 4.93-1.41 1.41"></path>
            </svg>
          </button>

          <div class="avatar" title="User account">
            ${escapeHtml(userInitials)}
          </div>
        </div>
      </div>
    `;

    this.root.querySelector(".waffle-btn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.open = !this.open;
      this.render();
    });

    this.root.querySelector(".purple-btn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent("cx3-open-purple-number"));
      this.dispatchEvent(new CustomEvent("purple-number-click", { bubbles: true, composed: true }));
    });

    this.root.querySelector(".theme-toggle")?.addEventListener("click", (e) => {
      e.stopPropagation();
      const isDark = document.documentElement.classList.toggle("dark");
      try {
        localStorage.setItem("theme", isDark ? "dark" : "light");
      } catch {}
      this.dispatchEvent(new CustomEvent("theme-toggle", {
        bubbles: true,
        composed: true,
        detail: { isDark },
      }));
    });

    const suiteApps = this.suiteApps();
    this.root.querySelectorAll<HTMLElement>("[data-app-id]").forEach((el) => {
      el.addEventListener("click", () => {
        const app = suiteApps.find((a) => a.id === el.dataset.appId);
        if (app) this.handleTileClick(app);
      });
    });
  }

  private renderPanel(): string {
    const currentAppId = this.currentAppIdAttr();
    const tiles = this.suiteApps().map((app) => {
      const isCurrent = app.id === currentAppId;
      const icon = ICONS[app.icon] ?? "";
      return `
        <div class="tile ${isCurrent ? "current" : ""}" data-app-id="${app.id}" title="${escapeHtml(app.tagline)}">
          <div class="tile-icon" style="background:${app.accent}">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icon}</svg>
          </div>
          <div class="tile-name">${escapeHtml(app.name)}</div>
          <div class="tile-status">${isCurrent ? "Active" : app.status === "connected" ? "Connected" : app.tagline}</div>
        </div>
      `;
    }).join("");

    return `
      <div class="panel">
        <div class="panel-header">Cybears Suite</div>
        <div class="grid">${tiles}</div>
      </div>
    `;
  }
}

// logo-src goes into an <img src>; allow only relative paths and http(s)/data:image
// URLs so a dynamic consumer can't smuggle in a javascript: or attribute-breaking value.
function safeImgSrc(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim();
  if (/^(https?:\/\/|\/(?!\/)|\.{0,2}\/|data:image\/(png|jpe?g|gif|webp|svg\+xml)[;,])/i.test(s)) return s;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(s) && !s.startsWith("//")) return s;
  return null;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

const STYLE = `
  :host {
    all: initial;
    display: block;
    position: relative;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }
  * { box-sizing: border-box; }

  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 16px;
    position: relative;
    z-index: 100;
    background: linear-gradient(180deg, #13091e 0%, #0c0515 100%);
    border-bottom: 1px solid rgba(139, 92, 246, 0.22);
    box-shadow: none;
    color: #f5eefa;
  }

  .left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .menu-anchor {
    position: relative;
    display: flex;
    align-items: center;
  }

  .waffle-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;
    border: 1px solid transparent;
    background: transparent;
    cursor: pointer;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    align-items: center;
    justify-items: center;
    gap: 3px;
    padding: 6.5px;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
  }
  .waffle-btn:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
  }
  .waffle-btn:active {
    transform: scale(0.94);
  }
  .waffle-btn[aria-expanded="true"] {
    background: rgba(147, 51, 234, 0.25);
    border-color: rgba(168, 85, 247, 0.5);
  }
  .waffle-btn span {
    display: block;
    width: 3.5px;
    height: 3.5px;
    border-radius: 1px;
    background: #ffffff;
    transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
  }
  .waffle-btn:hover span {
    background: #f3e8ff;
  }
  .waffle-btn[aria-expanded="true"] span {
    opacity: 0.55;
  }
  .waffle-btn[aria-expanded="true"] span:nth-child(5) {
    opacity: 1;
    background: #c084fc;
    transform: scale(1.35);
  }

  .logo {
    height: 22px;
    width: auto;
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }
  .wordmark {
    font-weight: 800;
    letter-spacing: 0.1em;
    font-size: 14px;
    color: #ffffff;
  }

  .divider {
    width: 1px;
    height: 22px;
    background: rgba(255, 255, 255, 0.18);
    margin: 0 4px;
    flex-shrink: 0;
  }

  .product-brand {
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 1;
    min-width: 0;
  }

  .product-title {
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: -0.01em;
    display: inline-flex;
    align-items: baseline;
  }

  .product-sup {
    font-size: 11px;
    font-weight: 700;
    color: #d946ef;
    margin-left: 1px;
    vertical-align: super;
    line-height: 0;
  }

  .product-accent-line {
    width: 100%;
    height: 2px;
    margin-top: 3px;
    margin-bottom: 2.5px;
    background: linear-gradient(90deg, #9333ea 0%, #c084fc 60%, rgba(192, 132, 252, 0.2) 100%);
    border-radius: 1px;
  }

  .product-subtitle {
    font-size: 8px;
    font-weight: 700;
    color: #38bdf8;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    white-space: nowrap;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .purple-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 12px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid rgba(168, 85, 247, 0.5);
    background: rgba(88, 28, 135, 0.22);
    color: #e9d5ff;
    font-size: 12px;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    text-decoration: none;
  }
  .purple-btn:hover {
    background: rgba(147, 51, 234, 0.35);
    border-color: rgba(192, 132, 252, 0.8);
    color: #ffffff;
    box-shadow: 0 0 14px rgba(168, 85, 247, 0.4);
  }
  .purple-btn:active {
    transform: scale(0.96);
  }

  .icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.04);
    color: #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
  }
  .icon-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.3);
    color: #ffffff;
  }
  .icon-btn:active {
    transform: scale(0.94);
  }
  .icon-btn svg {
    width: 14px;
    height: 14px;
  }

  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #7c3aed, #4f46e5);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    user-select: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    cursor: default;
  }

  .panel {
    position: absolute;
    top: 40px;
    left: 0;
    width: 330px;
    border-radius: 12px;
    overflow: hidden;
    z-index: 200;
    background: linear-gradient(165deg, #160c23 0%, #09040e 100%);
    border: 1px solid rgba(168, 85, 247, 0.35);
    box-shadow: 0 20px 48px -8px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(168, 85, 247, 0.15);
    color: #ece7f2;
    animation: suite-panel-in 0.15s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes suite-panel-in {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .panel-header {
    padding: 10px 14px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #c9a4e0;
    border-bottom: 1px solid rgba(168, 85, 247, 0.2);
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 10px;
  }
  .tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 4px;
    padding: 8px 4px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;
  }
  .tile.current {
    cursor: default;
    opacity: 0.95;
    background: rgba(168, 85, 247, 0.16);
    border: 1px solid rgba(168, 85, 247, 0.3);
  }
  .tile:not(.current):hover {
    background: rgba(168, 85, 247, 0.22);
    transform: translateY(-1px);
  }
  .tile-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .tile-icon svg {
    width: 18px;
    height: 18px;
  }
  .tile-name {
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
  }
  .tile-status {
    font-size: 9.5px;
    opacity: 0.65;
    color: #c4b5fd;
  }
`;

if (!customElements.get("cybears-suite-banner")) {
  customElements.define("cybears-suite-banner", CybearsSuiteBanner);
}
